// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import { createApp, h } from 'vue';
import moment from 'moment';
import CSDesignSystem, {
  axios,
  i18n,
  translator,
  overrideLoginTranslations
} from '@complispace/cs-design-system';
import VueGtag from 'vue-gtag';
import enLocale from '@/resources/i18n/en.json';
import { mapState } from 'vuex';
import * as types from '@/store/mutationTypes';
import SlotIndicator from './components/core/SlotIndicator';
import 'material-design-icons-iconfont/dist/material-design-icons.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import '@mdi/font/css/materialdesignicons.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import '@fortawesome/fontawesome-free/css/all.css';
import './main.css';

import App from './App';
// eslint-disable-next-line import/named
import router, { beforeEachRoute } from './router';
import store from './store';

import plugInAdapter from './lib/plugInAdapter';
import { vuetifyBuilder } from './lib/vuetifyBuilder';
/* eslint-enable */

/* If we need to supress Vue warnings for dynamic content changes
Vue.config.warnHandler = function customWarnHandler(err, vm, info) {
    // handle error
    // `info` is a Vue-specific error info, e.g. which lifecycle hook
    // the error was found in. Only available in 2.2.0+
    console.log('Custom vue warn handler: ', err, vm.name, info);
};
*/

const vuetify = vuetifyBuilder({ themeName: 'ce' }).build();

// Internationalization
const { messages } = i18n;
Object.keys(messages || {}).forEach((key) => {
  if (key === 'en') {
    const combined = { ...enLocale, ...messages[key] };
    const overridden = overrideLoginTranslations(combined, {
      loginViewTitle: enLocale.login.title,
      loginViewSubTitle: enLocale.login.subTitle
    });

    i18n.setLocaleMessage(key, overridden);
  } else {
    const merged = messages[key];
    const overridden = overrideLoginTranslations(merged, {
      loginViewTitle: enLocale.login.title,
      loginViewSubTitle: enLocale.login.subTitle
    });

    i18n.setLocaleMessage(key, overridden);
  }
});

const plugIns = new plugInAdapter.Adapter({
  env: process.env
});

const options = {
  store,
  router,
  beforeEachRoute,
  auth0: {
    appHost: window.location.host,
    appUrl: window.location.origin,
    auth0Domain: process.env.AUTH0_DOMAIN,
    auth0ClientId: process.env.AUTH0_CLIENT_ID,
    auth0Audience: process.env.AUTH0_AUDIENCE,
    appEnv: process.env.APP_ENV
  },
  login: {
    resetBtnTimeout: parseInt(process.env.VUE_APP_LOGIN_RESET_TIMEOUT || 5, 10)
  },
  gainsight: {
    gainsightSiteInfoApiHost: process.env.GAINSIGHT_SITE_INFO_API_HOST,
    gainsightProductTag: process.env.GAINSIGHT_PRODUCT_TAG
  }
};

const app = createApp({
  i18n,
  watch: {
    $route: () => {
      const { Appcues } = window;

      if (Appcues) window.Appcues.page();
    }
  },
  computed: {
    ...mapState({
      refreshToken: (state) => state.session.refreshToken,
      idpUri: (state) => state.session.idpUri
    })
  },
  mounted() {
    this.$el.addEventListener('click', this.onClickVue);
  },
  beforeDestroy() {
    this.$el.removeEventListener('click', this.onClickVue);
  },
  methods: {
    popupLogin() {
      if (this.refreshToken) {
        this.$store.commit(types.SESSION_SET_REFRESH_TOKEN, false);

        const refreshUri = `${window.location.origin}/static/refresh.html`;
        const queryParams = `client_id=quicklinks&redirect_uri=${encodeURIComponent(
          refreshUri
        )}&scope=blah&response_type=token&state=`;
        const url = `https://${this.idpUri}/oauth2/authorize?${queryParams}`;

        const height = 500;
        const width = 400;
        const ow = window.open(
          url,
          //          const ow = window.open("http://testschool.localhost:9083/static/test.html#launch",
          'Refresh Session',
          // eslint-disable-next-line no-restricted-globals
          `height=${height},width=${width},left=${(screen.width - width) / 2},top=${
            // eslint-disable-next-line no-restricted-globals
            (screen.height - height) / 2
          },resizable=yes,scrollbars=yes,toolbar=yes,menubar=no,location=no,directories=no, status=yes`
        );

        const that = this;

        window.addEventListener('message', function elcb(event) {
          // Do we trust the sender of this message?
          if (event.source !== ow) return;
          if (event.origin !== window.location.origin && !event.data.includes('access_token'))
            return;

          that.$store.dispatch('session/refresh', {
            uri: event.data
          });
          // FYI, hotjar and other things send 'message' so
          // must be sure we get the right one before removing
          window.removeEventListener('message', elcb);
          ow.close();
        });
      }
    },
    onClickVue() {
      this.popupLogin();
    }
  },
  render: () => h(App)
});

// Base Vue config
app.config.performance = true;
// eslint-disable-next-line no-multi-assign
app.config.globalProperties.$http = axios;
app.config.globalProperties.$app = { plugIns };

app.component('SlotIndicator', SlotIndicator);
const reqRes = require.context(`./resources/i18n`, true);
translator.applyAppI18n(reqRes);

app.use(vuetify);

app.use(CSDesignSystem, options);

app.use(router);
app.use(store);

app.config.globalProperties.$dateformat = (dataStr, pattern = 'YYYY-MM-DD HH:mm:ss') => {
  return moment(dataStr).format(pattern);
};

if (process.env.APP_ENV !== 'automation') {
  app.use(
    VueGtag,
    {
      config: {
        id: process.env.GOOGLE_ANALYTICS_ID,
        params: {
          send_page_view: false
        }
      }
    },
    router
  );
}

app.mount('#app');
